import React from 'react'
import { useTranslation } from 'react-i18next'
import { CustomerDetailsSection } from 'pages/Customers/ConfirmCustomer/CustomerDetails'
import { ConfirmCustomerDetail } from 'pages/Customers/helpers/types'
import { getCustomerAddress } from 'pages/Customers/helpers/utils'
import { ConfirmIndividualCustomerData, IndividualCustomerFormKey } from '../helpers/types'
import { getIndividualFieldLabel } from '../helpers/utils'

export interface ConfirmIndividualProps {
  individual: ConfirmIndividualCustomerData
}

export const ConfirmIndividual = ({ individual }: ConfirmIndividualProps) => {
  const { t } = useTranslation()
  const { data, displayData } = individual || {}
  const { taxIdentificationNumber, expectedMonthlyVolumeAmount, expectedMonthlyVolumeCurrency } =
    data || {}
  const { countryCode: country, taxResidenceCountryCode } = displayData || {}

  const getDataRows = (keys: IndividualCustomerFormKey[]): ConfirmCustomerDetail[] =>
    keys?.map(key => ({
      label: getIndividualFieldLabel(key),
      value: displayData?.[key] ?? data?.[key],
    })) ?? []

  const address = getCustomerAddress({ ...data, country }, { useAddressExcludingCountry: true })
  const taxResidenceCountry = taxResidenceCountryCode ? ` ${taxResidenceCountryCode}` : ''
  const taxIdentification = `${taxIdentificationNumber ?? ''}${taxResidenceCountry}`
  const volumeCurrency = expectedMonthlyVolumeCurrency ? ` ${expectedMonthlyVolumeCurrency}` : ''
  const expectedMonthlyVolume = `${expectedMonthlyVolumeAmount ?? ''}${volumeCurrency}`

  const basicDetails: ConfirmCustomerDetail[] = getDataRows([
    'firstName',
    'lastName',
    'dateOfBirth',
    'nationality',
    'emailAddress',
  ])

  const addressDetails: ConfirmCustomerDetail[] = [
    { label: t('address'), value: address },
    { label: t('country'), value: country },
  ]

  const complianceDetails: ConfirmCustomerDetail[] = [
    { label: t('customers.taxIdentification'), value: taxIdentification },
    ...getDataRows(['employmentStatus', 'sourceOfFunds', 'pepStatus', 'riskScore']),
    { label: t('customers.monthlyVolumes'), value: expectedMonthlyVolume },
    ...getDataRows(['useCase', 'description', 'externalReference']),
  ]

  return (
    <>
      <CustomerDetailsSection customerDetails={basicDetails} />
      <CustomerDetailsSection
        title={t('customers.residentialAddress')}
        customerDetails={addressDetails}
      />
      <CustomerDetailsSection
        title={t('customers.complianceInformation')}
        customerDetails={complianceDetails}
      />
    </>
  )
}
